import { useContext, useState, useCallback, useEffect, useMemo } from "react";
import type { BusinessSubscription, LensSDKException } from "lens-platform-sdk";
import { Button, ButtonBar, LoadingIndicator } from "@k8slens/lds";
import { FormSwitchGroup } from "@k8slens/lds-form";
import { SwitchValue } from "@k8slens/lds-form/lib/es/FormSwitchGroup/FormSwitchGroup";

import { ProfileContext } from "src/providers/profile-provider";
import { getUniqueEmailsArray } from "src/services/email";
import { PendingSubscriptionInvitation } from "src/services/getInvitationState";
import { isAssignableSubscription } from "src/services/getSubscriptionState";

import { OFFLINE_ACTIVATION } from "src/constants";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useCreateBusinessInvitation } from "src/hooks/useCreateBusinessInvitation";
import { useGetBusinessInvitations } from "src/hooks/useGetBusinessInvitations";
import { useAnalytics } from "src/hooks/useAnalytics";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useBusinessFeatures } from "src/hooks/useBusinessFeatures";
import { useBusinessUsers } from "src/hooks/useBusinessUsers";
import { ActivationLinkInvitation } from "src/hooks/useGetActivationLinkInvitation";
import { useDeleteBusinessInvitation } from "src/hooks/useDeleteBusinessInvitation";

import { BusinessContext } from "src/components/Business/Base";
import AssigneeTable from "src/components/Business/AssigneeTable";
import AssignSubscriptionModal from "src/components/Business/AssignSubscriptionModal";
import Notification from "src/components/Notification/Notification";
import { useConfirmDialog } from "src/components/ConfirmDialog/ConfirmDialog";

import styles from "./ManageSeats.module.css";

const assignSeatsLabel = "Assign Seats";

const offlineActivationTitle = "Offline Activation";
const offlineActivationLabel = "Enabled";
const offlineActivationDescription =
  "Offline Activation allows users in your Lens Business ID to activate Lens Desktop without an internet connection. This is useful for air-gapped environments or when you need to activate Lens Desktop on a machine that is not connected to the internet.";

/**
 * The "Manage Seats" page for the business, rendered at /business/:businessId/subscriptions
 */
const ManageSeats = () => {
  const { ConfirmDialog, confirm } = useConfirmDialog();
  const { track, trackError, trackButtonClicked } = useAnalytics();
  const { profile } = useContext(ProfileContext);
  const { businessId, business, reload: reloadBusiness } = useContext(BusinessContext);

  const {
    businessSubscriptions,
    loading: loadingBusinessSubscriptions,
    reload: reloadBusinessSubscriptions,
  } = useGetBusinessSubscriptions(businessId);
  const assignableBusinessSubscriptions = useMemo(
    () => businessSubscriptions.filter((subscription) => isAssignableSubscription(subscription)),
    [businessSubscriptions],
  );
  const {
    businessInvitations,
    loading: loadingBusinessInvitations,
    reload: reloadBusinessInvitations,
  } = useGetBusinessInvitations(businessId);
  const { businessUsers } = useBusinessUsers(businessId);
  const lensPlatformClient = useLensPlatformClient();
  const { createBusinessInvitation, loading: creatingBusinessInvitation } = useCreateBusinessInvitation(businessId);
  const [createBusinessInvitationError, setCreateBusinessInvitationError] = useState<LensSDKException>();
  const [seatsTableActiveTabIndex, setSeatsTableActiveTabIndex] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const { deleteBusinessInvitation, loading: deleteBusinessInvitationLoading } = useDeleteBusinessInvitation();

  const [assignSubscriptionModalOpen, setAssignSubscriptionModalOpen] = useState(false);
  const openAssignSubscriptionModal = useCallback(() => {
    trackButtonClicked(assignSeatsLabel);
    setAssignSubscriptionModalOpen(true);
  }, [trackButtonClicked]);
  const closeAssignSubscriptionModal = useCallback(() => {
    setAssignSubscriptionModalOpen(false);
    setCreateBusinessInvitationError(undefined);
  }, []);

  const onClickAssign = useCallback(
    async (emails: string, selectedSubscriptions: BusinessSubscription) => {
      const emailArray = getUniqueEmailsArray(emails);
      const subscriptionId = selectedSubscriptions.id;

      setCreateBusinessInvitationError(undefined);

      for (const email of emailArray) {
        // eslint-disable-next-line no-await-in-loop
        const exception = await createBusinessInvitation(email, subscriptionId);

        if (exception) {
          setCreateBusinessInvitationError(exception);
          trackError("Assigning Seats to Subscription Failed", {
            subscriptionId: selectedSubscriptions,
          });

          return exception;
        }
        track("Assigned Seats to Subscription", { subscriptionId: selectedSubscriptions });
      }
      await Promise.all([reloadBusinessSubscriptions(), reloadBusinessInvitations()]);
      closeAssignSubscriptionModal();

      // Set active tab to "Pending" to show assigned seat.
      if (seatsTableActiveTabIndex !== 1) {
        setSeatsTableActiveTabIndex(1);
      }

      return null;
    },
    [
      createBusinessInvitation,
      closeAssignSubscriptionModal,
      reloadBusinessSubscriptions,
      reloadBusinessInvitations,
      seatsTableActiveTabIndex,
      track,
      trackError,
    ],
  );

  const handleRemoveBusinessInvitation = async (
    invitation: PendingSubscriptionInvitation | ActivationLinkInvitation,
  ) => {
    if (!businessId || !invitation) {
      return;
    }

    await deleteBusinessInvitation(businessId, invitation.id, "link" in invitation);

    if (reloadBusiness) {
      await reloadBusiness(businessId);
    }
    await reloadBusinessInvitations();
  };

  const handleUnassignSeat = async (seatId: string) => {
    setError(null);
    setLoading(true);

    if (!businessId || !seatId) {
      return;
    }

    try {
      await lensPlatformClient.business.deActivateBusinessUserSubscription({
        businessId,
        businessSubscriptionId: seatId,
        username: profile?.username as string,
      });

      track("Seat unassigned", { seatId });

      await reloadBusinessSubscriptions();
      await reloadBusinessInvitations();
    } catch (error) {
      if (error instanceof Error) {
        setError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    businessFeatures,
    loadingBusinessFeatures,
    updateBusinessFeatures,
    updatingBusinessFeatures,
    errorLoadingBusinessFeatures,
    errorUpdatingBusinessFeatures,
  } = useBusinessFeatures(businessId);
  const loadingOfflineActivation = updatingBusinessFeatures || loadingBusinessFeatures;

  const [offlineActivationSwitches, setOfflineActivationSwitches] = useState<Array<SwitchValue>>();

  // Keep switch up to date with businessFeatures
  useEffect(() => {
    if (!businessFeatures) {
      return;
    }

    const d = businessFeatures.find(({ key }) => key === OFFLINE_ACTIVATION);

    if (!d) {
      return;
    }
    setOfflineActivationSwitches([
      {
        id: d.key,
        label: offlineActivationLabel,
        description: offlineActivationDescription,
        checked: d.enabled,
      },
    ]);
  }, [businessFeatures]);

  const handleSwitchChange = useCallback(
    async (nextSwitches: Array<SwitchValue>) => {
      const nextValue = nextSwitches.find(({ id }) => id === OFFLINE_ACTIVATION)?.checked;

      if (nextValue === undefined || !businessId) {
        return;
      }

      const confirmed =
        nextValue === true
          ? await confirm(
              "Are you sure you want to enable Offline Activation?",
              <>
                <p className="text-left">
                  {/* eslint-disable-next-line max-len */}
                  By enabling <strong>Offline Activation</strong> users in your Lens Business ID will be able to
                  download <strong>Offline Activation Codes</strong>.
                </p>
                <p className="!mb-0 text-left">
                  {/* eslint-disable-next-line max-len */}
                  The associated Lens Subscription of the user will be fixed until the end of the billing cycle and you
                  cannot re-assign the seat to other users.
                </p>
              </>,
              { confirmText: "Enable" },
            )
          : await confirm(
              "Are you sure you want to disable Offline Activation?",
              // eslint-disable-next-line max-len
              <>
                The seats currently using dowloaded <strong>Offline Activation Codes</strong> will remain fixed until
                the end of the billing cycle.
              </>,
              { confirmText: "Disable" },
            );

      if (confirmed) {
        updateBusinessFeatures({
          businessId,
          features: businessFeatures.map(({ key, enabled }) => ({
            key,
            enabled: key === OFFLINE_ACTIVATION ? nextValue : enabled,
          })),
        });
        trackButtonClicked(offlineActivationTitle);
      }
    },
    [businessFeatures, businessId, confirm, updateBusinessFeatures, trackButtonClicked],
  );

  return (
    <>
      <section className={styles.manageSeats}>
        <div>
          {(error || errorLoadingBusinessFeatures || errorUpdatingBusinessFeatures) && (
            <Notification
              type="flash"
              level="error"
              message={
                error?.message || errorLoadingBusinessFeatures?.message || errorUpdatingBusinessFeatures?.message
              }
            />
          )}
          <AssigneeTable
            businessSubscriptions={assignableBusinessSubscriptions}
            businessInvitations={businessInvitations}
            businessUsers={businessUsers}
            selectedTableTabIndex={seatsTableActiveTabIndex}
            setSelectedTableTabIndex={setSeatsTableActiveTabIndex}
            onRemoveBusinessInvitation={handleRemoveBusinessInvitation}
            onUnassignSeat={handleUnassignSeat}
            loading={loading || deleteBusinessInvitationLoading}
            business={business}
          />
          <ButtonBar className={styles.buttonBar}>
            <Button
              label={assignSeatsLabel}
              onClick={() => {
                openAssignSubscriptionModal();
              }}
              primary
              disabled={assignableBusinessSubscriptions.length === 0}
            />
          </ButtonBar>
        </div>
      </section>
      <section className={styles.offlineActivation}>
        <FormSwitchGroup
          wrapperProps={{ role: "group", "aria-labelledby": "feature-settings" }}
          label={offlineActivationTitle}
          values={offlineActivationSwitches || []}
          onChange={handleSwitchChange}
          disabled={loadingOfflineActivation}
        />
        {loadingOfflineActivation && <LoadingIndicator size="2xl" />}
      </section>
      <AssignSubscriptionModal
        open={assignSubscriptionModalOpen}
        onModalClose={closeAssignSubscriptionModal}
        onClickAssign={onClickAssign}
        businessSubscriptions={assignableBusinessSubscriptions}
        businessInvitations={businessInvitations}
        loadingBusinessSubscriptions={loadingBusinessSubscriptions}
        loadingBusinessInvitations={loadingBusinessInvitations}
        assigningSubscription={creatingBusinessInvitation}
        assignSubscriptionError={createBusinessInvitationError}
        disabled={creatingBusinessInvitation}
      />
      {ConfirmDialog}
    </>
  );
};

export default ManageSeats;
